/* eslint-disable max-len */
import React, {
  createContext, useContext, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';

// ── Languages ──
const LANGUAGES = {
  en: { key: 'en', label: 'English', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  es: { key: 'es', label: 'Espa\u00F1ol', flag: '\uD83C\uDDF2\uD83C\uDDFD' },
  pt: { key: 'pt', label: 'Portugu\u00EAs', flag: '\uD83C\uDDE7\uD83C\uDDF7' },
  zh: { key: 'zh', label: '\u7B80\u4F53\u4E2D\u6587', flag: '\uD83C\uDDE8\uD83C\uDDF3' },
  'zh-tw': { key: 'zh-tw', label: '\u7E41\u9AD4\u4E2D\u6587', flag: '\uD83C\uDDF9\uD83C\uDDFC' },
};

// ── UI strings per language ──
const UI = {
  en: {
    heroTitle: 'Built for Operators,',
    heroAccent: 'Loved by Players',
    heroSub: 'We bring together the world\u2019s top providers \u2014 Pragmatic Play, PG Soft, and Hacksaw \u2014 into a single integration. Hundreds of certified slots and casual games, all backed by proven math models and production quality.',
    exploreGames: 'Explore Games',
    requestInteg: 'Request Integration',
    whyTitle: 'Why PX Play',
    whySub: 'Everything operators need, nothing they do not.',
    singleApi: 'Single API',
    singleApiDesc: 'One integration, entire portfolio. RESTful API with docs and sandbox.',
    multiCurr: 'Multi-Currency',
    certRng: 'Certified RNG',
    readyTitle: 'Ready to integrate?',
    readyDesc: 'Get in touch and go live in days, not months.',
    contactUs: 'Contact Us',
    getInTouch: 'Get in Touch',
    topPerforming: 'Top Performing Games',
    topPerformingSub: 'Most popular games among our operators.',
    campaigns: 'Suggested Campaigns',
    campaignsSub: 'Ready-made packages based on real performance data.',
    navGames: 'Games',
    navAbout: 'About',
    navContact: 'Contact',
    gamePortfolio: 'Game Portfolio',
    gamesAvailable: 'games available for integration',
    searchGames: 'Search games...',
    filters: 'Filters',
    clearAll: 'Clear all',
    sortPopular: 'Popular',
    sortNew: 'New',
    sortMaxWin: 'Max Win',
    sortRtp: 'RTP',
    sortLabel: 'Sort:',
    gameType: 'Game Type',
    volatility: 'Volatility',
    maxWin: 'Max Win',
    gameFamily: 'Game Family',
    gameProvider: 'Provider',
    market: 'Market',
    all: 'All',
    // Feature cards
    singleInteg: 'Single Integration',
    singleIntegDesc: 'One API, entire portfolio. RESTful with full documentation, sandbox, and dedicated integration support.',
    currencies: 'Currencies',
    currenciesDesc: 'BRL, MXN, THB, USD, EUR and 50+ currencies including crypto. Configurable per operator.',
    avgRtp: 'Average RTP',
    avgRtpDesc: 'Certified math models with configurable RTP profiles. Independently tested and audited.',
    latency: 'Latency',
    latencyDesc: 'Sub-50ms response times. Global CDN, optimized game bundles, instant load on mobile.',
    uptimeTitle: 'Uptime',
    uptimeDesc: 'Production-grade infrastructure with auto-scaling, real-time monitoring, and 24/7 support.',
    goLive: 'Go-Live Time',
    goLiveDesc: 'From signed contract to live games in 48 hours. We handle the heavy lifting.',
    // Hero stats
    statGames: 'Providers',
    statMarkets: 'Markets',
    statOperators: 'Operators',
    statUptime: 'Uptime',
    // Game detail
    backToPortfolio: 'Back to Portfolio',
    launchDemo: 'Launch Demo',
    livePreview: 'Live Preview',
    hidePreview: 'Hide Preview',
    gamePreview: 'Game Preview',
    techSpecs: 'Technical Specifications',
    relatedGames: 'Related Games',
    moreFrom: 'More from the',
    series: 'Series',
    viewDetails: 'View Details',
    gameName: 'Game Name',
    provider: 'Provider',
    gameTypeLbl: 'Game Type',
    rtp: 'RTP',
    playersOnline: 'Players Online',
    // Footer
    lastUpdated: 'Last updated: April 2026',
    copyright: 'PX Play. All rights reserved.',
    // Contact
    contactTitle: 'Get in Touch',
    contactSub: 'Tell us about your operation and we will reach out within 24 hours.',
    contactName: 'Name',
    contactCompany: 'Company',
    contactEmail: 'Email',
    contactMessage: 'Message',
    sendMessage: 'Send Message',
    sending: 'Sending...',
    messageSent: 'Message sent',
    messageSentSub: 'Our team will get back to you shortly.',
    done: 'Done',
    noGamesFound: 'No games match your filters.',
    operatorStory: 'Operator Story',
    sweepstakesMode: 'Sweepstakes Mode',
    typeLbl: 'Type',
    typeSlot: 'Slot',
    typeCasual: 'Casual',
    volLow: 'Low',
    volMedium: 'Medium',
    volHigh: 'High',
  },
  es: {
    heroTitle: 'Hecho para Operadores,',
    heroAccent: 'Amado por los Jugadores',
    heroSub: 'Reunimos los mejores proveedores del mundo \u2014 Pragmatic Play, PG Soft y Hacksaw \u2014 en una sola integraci\u00F3n. Cientos de slots y juegos casuales certificados.',
    exploreGames: 'Explorar Juegos',
    requestInteg: 'Solicitar Integraci\u00F3n',
    whyTitle: 'Por qu\u00E9 PX Play',
    whySub: 'Todo lo que los operadores necesitan.',
    singleApi: 'API \u00DAnica',
    singleApiDesc: 'Una integraci\u00F3n, todo el portafolio. RESTful API con docs y sandbox.',
    multiCurr: 'Multi-Moneda',
    certRng: 'RNG Certificado',
    readyTitle: '\u00BFListo para integrar?',
    readyDesc: 'Cont\u00E1ctenos y comience a operar en d\u00EDas.',
    contactUs: 'Cont\u00E1ctenos',
    getInTouch: 'Cont\u00E1ctenos',
    topPerforming: 'Juegos Top',
    topPerformingSub: 'Los juegos m\u00E1s populares entre nuestros operadores.',
    campaigns: 'Campa\u00F1as Sugeridas',
    campaignsSub: 'Paquetes listos basados en rendimiento real.',
    navGames: 'Juegos',
    navAbout: 'Acerca',
    navContact: 'Contacto',
    gamePortfolio: 'Portafolio de Juegos',
    gamesAvailable: 'juegos disponibles para integraci\u00F3n',
    searchGames: 'Buscar juegos...',
    filters: 'Filtros',
    clearAll: 'Limpiar todo',
    sortPopular: 'Popular',
    sortNew: 'Nuevo',
    sortMaxWin: 'Max Win',
    sortRtp: 'RTP',
    sortLabel: 'Ordenar:',
    gameType: 'Tipo de Juego',
    volatility: 'Volatilidad',
    maxWin: 'Max Win',
    gameFamily: 'Familia de Juegos',
    market: 'Mercado',
    all: 'Todos',
    singleInteg: 'Integraci\u00F3n \u00DAnica',
    singleIntegDesc: 'Una API, todo el portafolio. RESTful con documentaci\u00F3n completa, sandbox y soporte dedicado.',
    currencies: 'Monedas',
    currenciesDesc: 'BRL, MXN, THB, USD, EUR y 50+ monedas incluyendo crypto. Configurable por operador.',
    avgRtp: 'RTP Promedio',
    avgRtpDesc: 'Modelos matem\u00E1ticos certificados con perfiles de RTP configurables. Probados y auditados.',
    latency: 'Latencia',
    latencyDesc: 'Tiempos de respuesta <50ms. CDN global, juegos optimizados, carga instant\u00E1nea en m\u00F3vil.',
    uptimeTitle: 'Disponibilidad',
    uptimeDesc: 'Infraestructura de grado productivo con auto-escalado, monitoreo en tiempo real y soporte 24/7.',
    goLive: 'Tiempo de Lanzamiento',
    goLiveDesc: 'Del contrato firmado a juegos en vivo en 48 horas. Nosotros hacemos el trabajo pesado.',
    statGames: 'Juegos',
    statMarkets: 'Mercados',
    statOperators: 'Operadores',
    statUptime: 'Disponibilidad',
    backToPortfolio: 'Volver al Portafolio',
    launchDemo: 'Lanzar Demo',
    livePreview: 'Vista Previa',
    hidePreview: 'Ocultar Vista',
    gamePreview: 'Vista del Juego',
    techSpecs: 'Especificaciones T\u00E9cnicas',
    relatedGames: 'Juegos Relacionados',
    moreFrom: 'M\u00E1s de la',
    series: 'Serie',
    viewDetails: 'Ver Detalles',
    gameName: 'Nombre del Juego',
    provider: 'Proveedor',
    gameTypeLbl: 'Tipo de Juego',
    rtp: 'RTP',
    playersOnline: 'Jugadores en L\u00EDnea',
    lastUpdated: '\u00DAltima actualizaci\u00F3n: Abril 2026',
    copyright: 'PX Play. Todos los derechos reservados.',
    contactTitle: 'Cont\u00E1ctenos',
    contactSub: 'Cu\u00E9ntenos sobre su operaci\u00F3n y nos comunicaremos dentro de 24 horas.',
    contactName: 'Nombre',
    contactCompany: 'Empresa',
    contactEmail: 'Correo',
    contactMessage: 'Mensaje',
    sendMessage: 'Enviar Mensaje',
    sending: 'Enviando...',
    messageSent: 'Mensaje enviado',
    messageSentSub: 'Nuestro equipo se comunicar\u00E1 pronto.',
    done: 'Listo',
    noGamesFound: 'Ning\u00FAn juego coincide con sus filtros.',
    operatorStory: 'Historia de Operador',
    sweepstakesMode: 'Modo Sorteo',
    typeLbl: 'Tipo',
    typeSlot: 'Tragamonedas',
    typeCasual: 'Casual',
    volLow: 'Baja',
    volMedium: 'Media',
    volHigh: 'Alta',
  },
  pt: {
    heroTitle: 'Feito para Operadores,',
    heroAccent: 'Amado pelos Jogadores',
    heroSub: 'Reunimos os melhores provedores do mundo \u2014 Pragmatic Play, PG Soft e Hacksaw \u2014 em uma \u00FAnica integra\u00E7\u00E3o. Centenas de slots e jogos casuais certificados.',
    exploreGames: 'Explorar Jogos',
    requestInteg: 'Solicitar Integra\u00E7\u00E3o',
    whyTitle: 'Por que PX Play',
    whySub: 'Tudo que operadores precisam.',
    singleApi: 'API \u00DAnica',
    singleApiDesc: 'Uma integra\u00E7\u00E3o, todo o portf\u00F3lio. RESTful API com docs e sandbox.',
    multiCurr: 'Multi-Moeda',
    certRng: 'RNG Certificado',
    readyTitle: 'Pronto para integrar?',
    readyDesc: 'Entre em contato e comece a operar em dias.',
    contactUs: 'Fale Conosco',
    getInTouch: 'Fale Conosco',
    topPerforming: 'Jogos Mais Populares',
    topPerformingSub: 'Os jogos mais populares entre nossos operadores.',
    campaigns: 'Campanhas Sugeridas',
    campaignsSub: 'Pacotes prontos baseados em performance real.',
    navGames: 'Jogos',
    navAbout: 'Sobre',
    navContact: 'Contato',
    gamePortfolio: 'Portf\u00F3lio de Jogos',
    gamesAvailable: 'jogos dispon\u00EDveis para integra\u00E7\u00E3o',
    searchGames: 'Buscar jogos...',
    filters: 'Filtros',
    clearAll: 'Limpar tudo',
    sortPopular: 'Popular',
    sortNew: 'Novo',
    sortMaxWin: 'Max Win',
    sortRtp: 'RTP',
    sortLabel: 'Ordenar:',
    gameType: 'Tipo de Jogo',
    volatility: 'Volatilidade',
    maxWin: 'Max Win',
    gameFamily: 'Fam\u00EDlia de Jogos',
    market: 'Mercado',
    all: 'Todos',
    singleInteg: 'Integra\u00E7\u00E3o \u00DAnica',
    singleIntegDesc: 'Uma API, todo o portf\u00F3lio. RESTful com documenta\u00E7\u00E3o completa, sandbox e suporte dedicado.',
    currencies: 'Moedas',
    currenciesDesc: 'BRL, MXN, THB, USD, EUR e 50+ moedas incluindo crypto. Configur\u00E1vel por operador.',
    avgRtp: 'RTP M\u00E9dio',
    avgRtpDesc: 'Modelos matem\u00E1ticos certificados com perfis de RTP configur\u00E1veis. Testados e auditados.',
    latency: 'Lat\u00EAncia',
    latencyDesc: 'Tempos de resposta <50ms. CDN global, jogos otimizados, carregamento instant\u00E2neo no mobile.',
    uptimeTitle: 'Disponibilidade',
    uptimeDesc: 'Infraestrutura de produ\u00E7\u00E3o com auto-scaling, monitoramento em tempo real e suporte 24/7.',
    goLive: 'Tempo para Go-Live',
    goLiveDesc: 'Do contrato assinado at\u00E9 jogos ao vivo em 48 horas. N\u00F3s cuidamos de tudo.',
    statGames: 'Jogos',
    statMarkets: 'Mercados',
    statOperators: 'Operadores',
    statUptime: 'Disponibilidade',
    backToPortfolio: 'Voltar ao Portf\u00F3lio',
    launchDemo: 'Lan\u00E7ar Demo',
    livePreview: 'Pr\u00E9via ao Vivo',
    hidePreview: 'Ocultar Pr\u00E9via',
    gamePreview: 'Pr\u00E9via do Jogo',
    techSpecs: 'Especifica\u00E7\u00F5es T\u00E9cnicas',
    relatedGames: 'Jogos Relacionados',
    moreFrom: 'Mais da',
    series: 'S\u00E9rie',
    viewDetails: 'Ver Detalhes',
    gameName: 'Nome do Jogo',
    provider: 'Provedor',
    gameTypeLbl: 'Tipo de Jogo',
    rtp: 'RTP',
    playersOnline: 'Jogadores Online',
    lastUpdated: '\u00DAltima atualiza\u00E7\u00E3o: Abril 2026',
    copyright: 'PX Play. Todos os direitos reservados.',
    contactTitle: 'Fale Conosco',
    contactSub: 'Conte-nos sobre sua opera\u00E7\u00E3o e entraremos em contato em 24 horas.',
    contactName: 'Nome',
    contactCompany: 'Empresa',
    contactEmail: 'E-mail',
    contactMessage: 'Mensagem',
    sendMessage: 'Enviar Mensagem',
    sending: 'Enviando...',
    messageSent: 'Mensagem enviada',
    messageSentSub: 'Nossa equipe entrar\u00E1 em contato em breve.',
    done: 'Pronto',
    noGamesFound: 'Nenhum jogo corresponde aos seus filtros.',
    operatorStory: 'Hist\u00F3ria de Operador',
    sweepstakesMode: 'Modo Sorteio',
    typeLbl: 'Tipo',
    typeSlot: 'Slot',
    typeCasual: 'Casual',
    volLow: 'Baixa',
    volMedium: 'M\u00E9dia',
    volHigh: 'Alta',
  },
  zh: {
    heroTitle: '\u4E13\u4E3A\u8FD0\u8425\u5546\u6253\u9020\uFF0C',
    heroAccent: '\u6DF1\u53D7\u73A9\u5BB6\u559C\u7231',
    heroSub: '\u6211\u4EEC\u6574\u5408\u5168\u7403\u9876\u7EA7\u4F9B\u5E94\u5546 \u2014 Pragmatic Play\u3001PG Soft \u548C Hacksaw \u2014 \u4E8E\u5355\u4E00\u63A5\u53E3\u3002\u6570\u767E\u6B3E\u8BA4\u8BC1\u8001\u864E\u673A\u548C\u4F11\u95F2\u6E38\u620F\uFF0C\u5747\u7ECF\u8FC7\u6570\u5B66\u6A21\u578B\u9A8C\u8BC1\u3002',
    exploreGames: '\u6D4F\u89C8\u6E38\u620F',
    requestInteg: '\u7533\u8BF7\u63A5\u5165',
    whyTitle: '\u4E3A\u4EC0\u4E48\u9009\u62E9 PX Play',
    whySub: '\u8FD0\u8425\u5546\u6240\u9700\u7684\u4E00\u5207\u3002',
    singleApi: '\u5355\u4E00API',
    singleApiDesc: '\u4E00\u6B21\u63A5\u5165\uFF0C\u5168\u90E8\u6E38\u620F\u3002RESTful API\uFF0C\u5B8C\u6574\u6587\u6863\u548C\u6D4B\u8BD5\u73AF\u5883\u3002',
    multiCurr: '\u591A\u5E01\u79CD',
    certRng: '\u8BA4\u8BC1RNG',
    readyTitle: '\u51C6\u5907\u597D\u63A5\u5165\u4E86\u5417\uFF1F',
    readyDesc: '\u8054\u7CFB\u6211\u4EEC\u7684\u63A5\u5165\u56E2\u961F\uFF0C\u51E0\u5929\u5185\u4E0A\u7EBF\u3002',
    contactUs: '\u8054\u7CFB\u6211\u4EEC',
    getInTouch: '\u8054\u7CFB\u6211\u4EEC',
    topPerforming: '\u70ED\u95E8\u6E38\u620F',
    topPerformingSub: '\u6700\u53D7\u8FD0\u8425\u5546\u6B22\u8FCE\u7684\u6E38\u620F\u3002',
    campaigns: '\u63A8\u8350\u8425\u9500\u65B9\u6848',
    campaignsSub: '\u57FA\u4E8E\u5B9E\u9645\u6570\u636E\u7684\u5957\u9910\u65B9\u6848\u3002',
    navGames: '\u6E38\u620F',
    navAbout: '\u5173\u4E8E',
    navContact: '\u8054\u7CFB',
    gamePortfolio: '\u6E38\u620F\u4EA7\u54C1',
    gamesAvailable: '\u6B3E\u6E38\u620F\u53EF\u63A5\u5165',
    searchGames: '\u641C\u7D22\u6E38\u620F...',
    filters: '\u7B5B\u9009',
    clearAll: '\u6E05\u9664\u5168\u90E8',
    sortPopular: '\u70ED\u95E8',
    sortNew: '\u6700\u65B0',
    sortMaxWin: '\u6700\u5927\u8D62\u53D6',
    sortRtp: 'RTP',
    sortLabel: '\u6392\u5E8F:',
    gameType: '\u6E38\u620F\u7C7B\u578B',
    volatility: '\u6CE2\u52A8\u6027',
    maxWin: '\u6700\u5927\u8D62\u53D6',
    gameFamily: '\u6E38\u620F\u7CFB\u5217',
    market: '\u5E02\u573A',
    all: '\u5168\u90E8',
    singleInteg: '\u5355\u4E00\u63A5\u5165',
    singleIntegDesc: '\u4E00\u4E2AAPI\uFF0C\u6574\u4E2A\u4EA7\u54C1\u7EBF\u3002RESTful\u63A5\u53E3\uFF0C\u5B8C\u6574\u6587\u6863\u3001\u6D4B\u8BD5\u73AF\u5883\u548C\u4E13\u5C5E\u63A5\u5165\u652F\u6301\u3002',
    currencies: '\u5E01\u79CD',
    currenciesDesc: 'BRL\u3001MXN\u3001THB\u3001USD\u3001EUR\u7B4950+\u79CD\u8D27\u5E01\uFF0C\u5305\u542B\u52A0\u5BC6\u8D27\u5E01\u3002\u53EF\u6309\u8FD0\u8425\u5546\u914D\u7F6E\u3002',
    avgRtp: '\u5E73\u5747RTP',
    avgRtpDesc: '\u7ECF\u8BA4\u8BC1\u7684\u6570\u5B66\u6A21\u578B\uFF0C\u53EFRTP\u914D\u7F6E\u6587\u4EF6\u3002\u72EC\u7ACB\u6D4B\u8BD5\u548C\u5BA1\u8BA1\u3002',
    latency: '\u5EF6\u8FDF',
    latencyDesc: '<50ms\u54CD\u5E94\u65F6\u95F4\u3002\u5168\u7403CDN\uFF0C\u4F18\u5316\u6E38\u620F\u5305\uFF0C\u79FB\u52A8\u7AEF\u77AC\u65F6\u52A0\u8F7D\u3002',
    uptimeTitle: '\u8FD0\u884C\u65F6\u95F4',
    uptimeDesc: '\u751F\u4EA7\u7EA7\u57FA\u7840\u8BBE\u65BD\uFF0C\u81EA\u52A8\u6269\u5C55\u3001\u5B9E\u65F6\u76D1\u63A7\u548C7x24\u5C0F\u65F6\u652F\u6301\u3002',
    goLive: '\u4E0A\u7EBF\u65F6\u95F4',
    goLiveDesc: '\u4ECE\u7B7E\u7EA6\u5230\u6E38\u620F\u4E0A\u7EBF\u53EA\u970048\u5C0F\u65F6\u3002\u6211\u4EEC\u5904\u7406\u6240\u6709\u6280\u672F\u5DE5\u4F5C\u3002',
    statGames: '\u6E38\u620F',
    statMarkets: '\u5E02\u573A',
    statOperators: '\u8FD0\u8425\u5546',
    statUptime: '\u8FD0\u884C\u65F6\u95F4',
    backToPortfolio: '\u8FD4\u56DE\u4EA7\u54C1\u5E93',
    launchDemo: '\u542F\u52A8\u6F14\u793A',
    livePreview: '\u5B9E\u65F6\u9884\u89C8',
    hidePreview: '\u9690\u85CF\u9884\u89C8',
    gamePreview: '\u6E38\u620F\u9884\u89C8',
    techSpecs: '\u6280\u672F\u89C4\u683C',
    relatedGames: '\u76F8\u5173\u6E38\u620F',
    moreFrom: '\u66F4\u591A',
    series: '\u7CFB\u5217',
    viewDetails: '\u67E5\u770B\u8BE6\u60C5',
    gameName: '\u6E38\u620F\u540D\u79F0',
    provider: '\u63D0\u4F9B\u5546',
    gameTypeLbl: '\u6E38\u620F\u7C7B\u578B',
    rtp: 'RTP',
    playersOnline: '\u5728\u7EBF\u73A9\u5BB6',
    lastUpdated: '\u6700\u540E\u66F4\u65B0: 2026\u5E744\u6708',
    copyright: 'PX Play\u3002\u7248\u6743\u6240\u6709\u3002',
    contactTitle: '\u8054\u7CFB\u6211\u4EEC',
    contactSub: '\u544A\u8BC9\u6211\u4EEC\u60A8\u7684\u8FD0\u8425\u60C5\u51B5\uFF0C\u6211\u4EEC\u5C0624\u5C0F\u65F6\u5185\u56DE\u590D\u3002',
    contactName: '\u59D3\u540D',
    contactCompany: '\u516C\u53F8',
    contactEmail: '\u90AE\u7BB1',
    contactMessage: '\u6D88\u606F',
    sendMessage: '\u53D1\u9001\u6D88\u606F',
    sending: '\u53D1\u9001\u4E2D...',
    messageSent: '\u6D88\u606F\u5DF2\u53D1\u9001',
    messageSentSub: '\u6211\u4EEC\u7684\u56E2\u961F\u5C06\u5C3D\u5FEB\u56DE\u590D\u60A8\u3002',
    done: '\u5B8C\u6210',
    noGamesFound: '\u6CA1\u6709\u7B26\u5408\u7B5B\u9009\u6761\u4EF6\u7684\u6E38\u620F\u3002',
    operatorStory: '\u8FD0\u8425\u5546\u6848\u4F8B',
    sweepstakesMode: '\u62BD\u5956\u6A21\u5F0F',
    typeLbl: '\u7C7B\u578B',
    typeSlot: '\u8001\u864E\u673A',
    typeCasual: '\u4F11\u95F2',
    volLow: '\u4F4E',
    volMedium: '\u4E2D',
    volHigh: '\u9AD8',
  },
  'zh-tw': {
    heroTitle: '專為營運商打造，',
    heroAccent: '深受玩家喜愛',
    heroSub: '我們整合全球頂級供應商 — Pragmatic Play、PG Soft 和 Hacksaw — 於單一介面。數百款認證老虎機和休閒遊戲，均經過數學模型驗證。',
    exploreGames: '瀏覽遊戲',
    requestInteg: '申請接入',
    whyTitle: '為什麼選擇 PX Play',
    whySub: '營運商所需的一切。',
    singleApi: '單一API',
    singleApiDesc: '一次接入，全部遊戲。RESTful API，完整文件和測試環境。',
    multiCurr: '多幣種',
    certRng: '認證RNG',
    readyTitle: '準備好接入了嗎？',
    readyDesc: '聯繫我們的接入團隊，幾天內上線。',
    contactUs: '聯繫我們',
    getInTouch: '聯繫我們',
    topPerforming: '熱門遊戲',
    topPerformingSub: '最受營運商歡迎的遊戲。',
    campaigns: '推薦營銷方案',
    campaignsSub: '基於實際數據的套餐方案。',
    navGames: '遊戲',
    navAbout: '關於',
    navContact: '聯繫',
    gamePortfolio: '遊戲產品',
    gamesAvailable: '款遊戲可接入',
    searchGames: '搜尋遊戲...',
    filters: '篩選',
    clearAll: '清除全部',
    sortPopular: '熱門',
    sortNew: '最新',
    sortMaxWin: '最大贏取',
    sortRtp: 'RTP',
    sortLabel: '排序:',
    gameType: '遊戲類型',
    volatility: '波動性',
    maxWin: '最大贏取',
    gameFamily: '遊戲系列',
    market: '市場',
    all: '全部',
    singleInteg: '單一接入',
    singleIntegDesc: '一個API，整個產品線。RESTful介面，完整文件、測試環境和專屬接入支援。',
    currencies: '幣種',
    currenciesDesc: 'BRL、MXN、THB、USD、EUR等50+種貨幣，包含加密貨幣。可按營運商配置。',
    avgRtp: '平均RTP',
    avgRtpDesc: '經認證的數學模型，可RTP配置檔。獨立測試和審計。',
    latency: '延遲',
    latencyDesc: '<50ms回應時間。全球CDN，最佳化遊戲包，行動端即時載入。',
    uptimeTitle: '運行時間',
    uptimeDesc: '生產級基礎設施，自動擴展、即時監控和7x24小時支援。',
    goLive: '上線時間',
    goLiveDesc: '從簽約到遊戲上線僅需48小時。我們處理所有技術工作。',
    statGames: '遊戲',
    statMarkets: '市場',
    statOperators: '營運商',
    statUptime: '運行時間',
    backToPortfolio: '返回產品庫',
    launchDemo: '啟動演示',
    livePreview: '即時預覽',
    hidePreview: '隱藏預覽',
    gamePreview: '遊戲預覽',
    techSpecs: '技術規格',
    relatedGames: '相關遊戲',
    moreFrom: '更多',
    series: '系列',
    viewDetails: '查看詳情',
    gameName: '遊戲名稱',
    provider: '提供商',
    gameTypeLbl: '遊戲類型',
    rtp: 'RTP',
    playersOnline: '線上玩家',
    lastUpdated: '最後更新: 2026年4月',
    copyright: 'PX Play。版權所有。',
    contactTitle: '聯繫我們',
    contactSub: '告訴我們您的營運情況，我們將在24小時內回覆。',
    contactName: '姓名',
    contactCompany: '公司',
    contactEmail: '電子信箱',
    contactMessage: '訊息',
    sendMessage: '發送訊息',
    sending: '發送中...',
    messageSent: '訊息已發送',
    messageSentSub: '我們的團隊將盡快回覆您。',
    done: '完成',
    noGamesFound: '沒有符合篩選條件的遊戲。',
    operatorStory: '營運商案例',
    sweepstakesMode: '抽獎模式',
    typeLbl: '類型',
    typeSlot: '老虎機',
    typeCasual: '休閒',
    volLow: '低',
    volMedium: '中',
    volHigh: '高',
  },
};

// ── Markets (geographic, no language) ──
const MARKETS = {
  global: { key: 'global', label: 'Global', flag: '\uD83C\uDF10' },
  brazil: { key: 'brazil', label: 'Brasil', flag: '\uD83C\uDDE7\uD83C\uDDF7' },
  mexico: { key: 'mexico', label: 'M\u00E9xico', flag: '\uD83C\uDDF2\uD83C\uDDFD' },
  asia: { key: 'asia', label: 'Asia', flag: '\uD83C\uDDF9\uD83C\uDDED' },
  europe: { key: 'europe', label: 'Europe', flag: '\uD83C\uDDEA\uD83C\uDDFA' },
};

// ── Market-specific feature text ──
const MARKET_FEATURES = {
  global: {
    currency: 'Multi-Currency support for 50+ currencies including crypto.',
    compliance: 'Full regulatory compliance across multiple jurisdictions.',
    highlight: 'Configurable RTP profiles per market.',
  },
  brazil: {
    currency: 'BRL nativo, integra\u00E7\u00E3o PIX, suporte a crypto.',
    compliance: 'Pronto para regulamenta\u00E7\u00E3o brasileira (SIGAP/SPA).',
    highlight: 'Temas que performam no mercado BR.',
  },
  mexico: {
    currency: 'MXN nativo, USD, y soporte multi-moneda.',
    compliance: 'Listo para SEGOB y regulaci\u00F3n mexicana.',
    highlight: 'Temas populares en el mercado MX.',
  },
  asia: {
    currency: 'THB, VND, MYR, IDR, PHP and 30+ currencies.',
    compliance: 'Licensed across SEA jurisdictions.',
    highlight: 'Asian-themed games with proven metrics.',
  },
  europe: {
    currency: 'EUR, GBP, SEK, NOK, and 20+ currencies.',
    compliance: 'MGA, UKGC, multi-jurisdiction ready.',
    highlight: 'Responsible gaming features built in.',
  },
};

const MarketContext = createContext();

export function MarketProvider({ children }) {
  const [market, setMarket] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMkt = params.get('market');
    if (urlMkt && MARKETS[urlMkt]) return urlMkt;
    return 'global';
  });
  const [version, setVersion] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('version');
    return v === 'sweepstakes' ? 'sweepstakes' : 'realmoney';
  });
  const [lang, setLang] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang && LANGUAGES[urlLang]) return urlLang;
    return 'en';
  });

  const ui = UI[lang] || UI.en;
  const mktData = MARKETS[market] || MARKETS.global;
  const features = MARKET_FEATURES[market] || MARKET_FEATURES.global;

  const value = useMemo(
    () => ({
      market,
      setMarket,
      mktData,
      features,
      markets: MARKETS,
      lang,
      setLang,
      ui,
      languages: LANGUAGES,
      version,
      setVersion,
    }),
    [market, mktData, features, lang, ui, version],
  );

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
}

MarketProvider.propTypes = {
  children: PropTypes.node,
};

export function useMarket() {
  return useContext(MarketContext);
}

export default MarketContext;
